import React, { useState } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
} from 'aws-amplify/auth';
import TopQikLogo from '../assets/GoalCastLogo';
import { COUNTRIES, countryCodeToFlag } from '../data/countries';
import { GOLDEN_BOOT_PLAYERS, GOLDEN_BALL_PLAYERS, GOLDEN_GLOVE_PLAYERS } from '../data/wcPlayers';
import { getWcLockState } from '../data/wcLock';
import './AuthPage.css';

type View = 'signIn' | 'signUp' | 'confirmSignUp' | 'forgotPassword' | 'confirmReset' | 'wcProfile';

export default function AuthPage() {
  const [view, setView] = useState<View>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // WC 2026 profile step
  const [wcCountry, setWcCountry] = useState('');
  const [goldenBoot, setGoldenBoot] = useState('');
  const [goldenBall, setGoldenBall] = useState('');
  const [goldenGlove, setGoldenGlove] = useState('');

  const clearMessages = () => { setError(null); setInfo(null); };

  const switchTab = (tab: 'signIn' | 'signUp') => {
    setView(tab);
    clearMessages();
  };

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await signIn({ username: email, password });
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await signUp({ username: email, password, options: { userAttributes: { email }, autoSignIn: false } });
      setInfo('Check your email for a verification code.');
      setView('confirmSignUp');
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmSignUp(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      // Skip predictions step if tournament already started
      if (getWcLockState().isLocked) {
        await signIn({ username: email, password });
      } else {
        setView('wcProfile');
      }
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleWcProfileSave() {
    setLoading(true);
    try {
      const picks: Record<string, string> = {};
      if (wcCountry) {
        picks.supportingCountry = wcCountry;
        picks.supportingCountryName = COUNTRIES.find(c => c.code === wcCountry)?.name ?? wcCountry;
      }
      if (goldenBoot) picks.goldenBoot = goldenBoot;
      if (goldenBall) picks.goldenBall = goldenBall;
      if (goldenGlove) picks.goldenGlove = goldenGlove;
      if (Object.keys(picks).length > 0) {
        localStorage.setItem('topqik_pending_wc_picks', JSON.stringify(picks));
      }
      await signIn({ username: email, password });
    } catch (err: any) {
      setError(friendlyError(err));
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setInfo('Reset code sent — check your email.');
      setView('confirmReset');
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmReset(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setInfo('Password reset! Please sign in.');
      setView('signIn');
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    clearMessages();
    try {
      await resendSignUpCode({ username: email });
      setInfo('New code sent to your email.');
    } catch (err: any) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="auth-root">
      {/* Animated background balls */}
      <div className="bg-balls" aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`bg-ball bg-ball-${i + 1}`} />
        ))}
      </div>

      {/* Logo */}
      <div className="auth-logo">
        <TopQikLogo height={90} className="auth-logo-svg" />
      </div>
      <p className="auth-tagline">Predict. Beat the AI. Dominate the table.</p>

      {/* Card */}
      <div className="auth-card">

        {/* Pill tabs — sign in / create account */}
        {(view === 'signIn' || view === 'signUp') && (
          <div className="auth-tabs">
            <button
              className={`auth-tab${view === 'signIn' ? ' auth-tab--active' : ''}`}
              onClick={() => switchTab('signIn')}
              type="button"
            >
              Sign In
            </button>
            <button
              className={`auth-tab${view === 'signUp' ? ' auth-tab--active' : ''}`}
              onClick={() => switchTab('signUp')}
              type="button"
            >
              Create Account
            </button>
          </div>
        )}

        {/* Back link for sub-views */}
        {(view === 'confirmSignUp' || view === 'forgotPassword' || view === 'confirmReset' || view === 'wcProfile') && (
          <button className="auth-back" onClick={() => { setView('signIn'); clearMessages(); }} type="button">
            ← Back to Sign In
          </button>
        )}

        {error && <div className="auth-msg auth-msg--error">{error}</div>}
        {info && <div className="auth-msg auth-msg--info">{info}</div>}

        {/* Sign In */}
        {view === 'signIn' && (
          <form onSubmit={handleSignIn} className="auth-form">
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
            <button className="auth-cta" type="submit" disabled={loading}>
              {loading ? <Spinner /> : 'Sign In'}
            </button>
            <button
              className="auth-forgot"
              type="button"
              onClick={() => { setView('forgotPassword'); clearMessages(); }}
            >
              Forgot password?
            </button>
          </form>
        )}

        {/* Sign Up */}
        {view === 'signUp' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="Min 8 chars, upper, lower, digit" />
            <button className="auth-cta" type="submit" disabled={loading}>
              {loading ? <Spinner /> : 'Create Account'}
            </button>
            <div className="auth-email-notice">
              <span className="auth-email-notice-icon">✉️</span>
              <span>A verification code will be emailed to you. If it doesn't arrive within a minute, please check your <strong>spam or junk folder</strong>.</span>
            </div>
          </form>
        )}

        {/* Confirm sign-up code */}
        {view === 'confirmSignUp' && (
          <form onSubmit={handleConfirmSignUp} className="auth-form">
            <p className="auth-hint">A 6-digit code was sent to <strong>{email}</strong></p>
            <div className="auth-email-notice">
              <span className="auth-email-notice-icon">✉️</span>
              <span>Can't find it? Check your <strong>spam or junk folder</strong> — verification emails occasionally get filtered. You can also request a new code below.</span>
            </div>
            <Field label="Verification Code" type="text" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" />
            <button className="auth-cta" type="submit" disabled={loading}>
              {loading ? <Spinner /> : 'Verify Email →'}
            </button>
            <button className="auth-forgot" type="button" onClick={handleResend}>
              Resend code
            </button>
          </form>
        )}

        {/* World Cup 2026 Profile */}
        {view === 'wcProfile' && (() => {
          const lockState = getWcLockState();
          return (
          <div className="auth-wc">
            <div className="auth-wc-header">
              <span className="auth-wc-trophy">🏆</span>
              <div>
                <div className="auth-wc-title">World Cup 2026 Predictions</div>
                <div className="auth-wc-sub">All optional — editable on your profile until kickoff</div>
              </div>
            </div>
            <div className={`auth-wc-lock-notice${lockState.urgency === 'soon' ? ' auth-wc-lock-notice--urgent' : ''}`}>
              <span className="auth-wc-lock-icon">⏰</span>
              <span>{lockState.label} — predictions <strong>lock on June 15</strong> — can't be changed after that.</span>
            </div>

            <div className="auth-wc-fields">
              <div className="auth-wc-field">
                <label className="auth-wc-label">🌍 Which nation are you supporting?</label>
                <select className="auth-wc-select" value={wcCountry} onChange={e => setWcCountry(e.target.value)}>
                  <option value="">Select a nation…</option>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{countryCodeToFlag(c.code)} {c.name}</option>
                  ))}
                </select>
              </div>

              <div className="auth-wc-field">
                <label className="auth-wc-label">🥾 Golden Boot — Top Scorer</label>
                <select className="auth-wc-select" value={goldenBoot} onChange={e => setGoldenBoot(e.target.value)}>
                  <option value="">Select a player…</option>
                  {GOLDEN_BOOT_PLAYERS.map(p => (
                    <option key={p.name} value={p.name}>{p.name} · {p.country}</option>
                  ))}
                </select>
              </div>

              <div className="auth-wc-field">
                <label className="auth-wc-label">⚽ Golden Ball — Best Player</label>
                <select className="auth-wc-select" value={goldenBall} onChange={e => setGoldenBall(e.target.value)}>
                  <option value="">Select a player…</option>
                  {GOLDEN_BALL_PLAYERS.map(p => (
                    <option key={p.name} value={p.name}>{p.name} · {p.country}</option>
                  ))}
                </select>
              </div>

              <div className="auth-wc-field">
                <label className="auth-wc-label">🧤 Golden Glove — Best Goalkeeper</label>
                <select className="auth-wc-select" value={goldenGlove} onChange={e => setGoldenGlove(e.target.value)}>
                  <option value="">Select a player…</option>
                  {GOLDEN_GLOVE_PLAYERS.map(p => (
                    <option key={p.name} value={p.name}>{p.name} · {p.country}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="auth-cta" onClick={handleWcProfileSave} disabled={loading}>
              {loading ? <Spinner /> : 'Save & Start Playing →'}
            </button>
            <button className="auth-forgot" type="button" onClick={handleWcProfileSave} disabled={loading}>
              Skip for now
            </button>
          </div>
          );
        })()}

        {/* Forgot password — enter email */}
        {view === 'forgotPassword' && (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <p className="auth-hint">Enter your account email and we'll send a 6-digit reset code.</p>
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <button className="auth-cta" type="submit" disabled={loading}>
              {loading ? <Spinner /> : 'Send Reset Code'}
            </button>
            <div className="auth-email-notice">
              <span className="auth-email-notice-icon">✉️</span>
              <span>The reset code is sent by email. If it doesn't arrive within a minute, check your <strong>spam or junk folder</strong>.</span>
            </div>
          </form>
        )}

        {/* Confirm reset — code + new password */}
        {view === 'confirmReset' && (
          <form onSubmit={handleConfirmReset} className="auth-form">
            <p className="auth-hint">Enter the 6-digit code sent to <strong>{email}</strong> and choose a new password.</p>
            <div className="auth-email-notice">
              <span className="auth-email-notice-icon">✉️</span>
              <span>Can't find the code? Check your <strong>spam or junk folder</strong>. Codes expire after 24 hours.</span>
            </div>
            <Field label="Reset Code" type="text" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" />
            <Field label="New Password" type="password" value={newPassword} onChange={setNewPassword} placeholder="Min 8 chars, upper, lower, digit" />
            <button className="auth-cta" type="submit" disabled={loading}>
              {loading ? <Spinner /> : 'Reset Password'}
            </button>
          </form>
        )}
      </div>

      <p className="auth-footer">TopQik · AI picks first. You pick better.</p>
    </div>
  );
}

/* ── Helpers ── */

interface FieldProps {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

function Field({ label, type, value, onChange, placeholder, inputMode }: FieldProps) {
  return (
    <div className="auth-field">
      <label className="auth-label">{label}</label>
      <input
        className="auth-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        required
        autoComplete={type === 'password' ? 'current-password' : 'email'}
      />
    </div>
  );
}

function Spinner() {
  return <span className="auth-spinner" aria-label="Loading" />;
}


function friendlyError(err: any): string {
  const msg: string = err?.message ?? String(err);
  if (msg.includes('UserNotFoundException') || msg.includes('Incorrect username or password'))
    return 'Incorrect email or password.';
  if (msg.includes('UsernameExistsException'))
    return 'An account with this email already exists.';
  if (msg.includes('CodeMismatchException'))
    return 'That code is incorrect. Please try again.';
  if (msg.includes('ExpiredCodeException'))
    return 'That code has expired. Please request a new one.';
  if (msg.includes('InvalidPasswordException') || msg.includes('password'))
    return 'Password must be at least 8 characters with uppercase, lowercase, and a digit.';
  if (msg.includes('LimitExceededException'))
    return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('NetworkError') || msg.includes('fetch'))
    return 'Network error — check your connection.';
  return msg;
}
